import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const Route = createFileRoute('/_authed/community/rooms')({
  component: RoomsList,
});

interface RoomListItem {
  id: string;
  theme: string;
  description: string | null;
  maxMembers: number;
  memberCount: number;
  status: string;
}

async function fetchJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
    ...init,
  });
  if (!res.ok) throw new Error(`${path} -> ${res.status}: ${await res.text()}`);
  return (await res.json()) as T;
}

function RoomsList() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['rooms'],
    queryFn: () => fetchJson<{ rooms: RoomListItem[] }>('/api/community/rooms'),
  });

  const join = useMutation({
    mutationFn: (id: string) =>
      fetchJson<{ pseudonym: string }>(`/api/community/rooms/${id}/join`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rooms'] }),
  });

  return (
    <section className="mx-auto max-w-2xl px-4 py-12">
      <h1 className="text-2xl font-semibold tracking-tight">rooms</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        small, themed, ai-facilitated. you appear under a pseudonym.
      </p>
      <ul className="mt-6 space-y-2">
        {(list.data?.rooms ?? []).map((r) => (
          <li key={r.id} className="rounded-[var(--radius)] border border-border p-4">
            <div className="flex items-baseline justify-between gap-3">
              <div>
                <div className="font-medium">{r.theme}</div>
                {r.description && <div className="text-sm text-muted-foreground">{r.description}</div>}
              </div>
              <div className="text-xs text-muted-foreground">
                {r.memberCount}/{r.maxMembers}
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={() => join.mutate(r.id)}
                disabled={join.isPending || r.memberCount >= r.maxMembers}
                className="text-sm underline disabled:opacity-50"
              >
                {r.memberCount >= r.maxMembers ? 'full' : 'join'}
              </button>
              <Link
                to="/community/rooms/$id"
                params={{ id: r.id }}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                open →
              </Link>
            </div>
          </li>
        ))}
        {(list.data?.rooms ?? []).length === 0 && (
          <li className="text-sm text-muted-foreground">no open rooms.</li>
        )}
      </ul>
    </section>
  );
}
